import { FC, useCallback } from "react";
import { useSelector } from "react-redux";
import { EmptyMessage } from "src/components";
import { PdTableCellText } from "src/components/layout/table";
import { useFocusableQuery } from "src/hooks/api";
import { tColumn } from "src/hooks/table";
import api from "src/util/apiNew";
import { formatLongAddress, formatMoney } from "src/util/gFormatting";
import { tSalesBySkuReport } from "tmt_api/src/types/dtos";

import { PdSpinner, ReportTable } from "./components";
import { formatDate, getParams } from "./utils";

const getSalesBySkuReport = (start: Date, end: Date) =>
  api.get<tSalesBySkuReport[]>("reports/sales-by-sku", getParams(start, end)).then((res) => res.data);

const columns: tColumn<tSalesBySkuReport>[] = [
  {
    id: "orderNumber",
    header: "Order #",
    accessor: (originalRow) => originalRow.orderNumber,
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 120 },
  },
  {
    id: "orderDateInSeconds",
    header: "Order Date",
    accessor: (originalRow) => formatDate(originalRow.orderDateInSeconds),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 120 },
  },
  {
    id: "brandName",
    header: "Brand Name",
    accessor: (originalRow) => originalRow.brandName,
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "retailerName",
    header: "Business Name",
    accessor: (originalRow) => originalRow.retailerName,
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "shippingAddress.address1",
    header: "Address",
    accessor: (originalRow) => formatLongAddress(originalRow.shippingAddress),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "productName",
    header: "Product Name",
    accessor: (originalRow) => originalRow.productName,
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "sku",
    header: "SKU",
    accessor: (originalRow) => originalRow.sku,
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "quantity",
    header: "Quantity",
    accessor: (originalRow) => originalRow.quantity,
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "wholesalePrice",
    header: "Wholesale Price",
    accessor: (originalRow) => formatMoney(originalRow.wholesalePrice),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 120 },
  },
  {
    id: "msrpPrice",
    header: "Retail Price",
    accessor: (originalRow) => formatMoney(originalRow.msrpPrice),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 120 },
  },
  {
    id: "orderSource",
    header: "Order Source",
    accessor: (originalRow) => originalRow.orderSource,
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 180 },
  },
  {
    id: "orderStatus",
    header: "Order Status",
    accessor: (originalRow) => originalRow.orderStatus,
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 180 },
  },
  {
    id: "salesRepName",
    header: "Sales Rep Name (if applicable)",
    accessor: (originalRow) => originalRow.salesRepName || "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "purchaseOrderNumber",
    header: "PO # (if applicable)",
    accessor: (originalRow) => originalRow.purchaseOrderNumber || "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "promotionCode",
    header: "Promotion code (if applicable)",
    accessor: (originalRow) => originalRow.promotionCode || "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 300 },
  },
  {
    id: "promotionAmount",
    header: "Promotion discount $ value (if applicable)",
    accessor: (originalRow) => formatMoney(originalRow.promotionAmount),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 400 },
  },
];

const TotalSalesBySkuTable: FC<{ period: Date[] }> = ({ period }) => {
  const config = useSelector((state) => state.TableConfigReducer.totalSalesBySKU);
  const { isLoading, data } = useFocusableQuery<tSalesBySkuReport[]>(
    useCallback(() => getSalesBySkuReport(period[0], period[1]), [period]),
    { enabled: !!(period[0] && period[1]) },
  );

  if (isLoading) {
    return <PdSpinner />;
  }

  if (!data || data?.length === 0) {
    return <EmptyMessage message="There is no data to display" />;
  }

  return (
    <ReportTable
      data={data}
      columns={columns}
      keyExtractor={(item) => `${item.orderNumber}-${item.sku}`}
      sortedBy="orderDateInSeconds"
      columnsConfig={config}
      tableConfigReduxKey="totalSalesBySKU"
    />
  );
};

export default TotalSalesBySkuTable;
